import _class_call_check from "@swc/helpers/src/_class_call_check.mjs";
import _sliced_to_array from "@swc/helpers/src/_sliced_to_array.mjs";
import _to_consumable_array from "@swc/helpers/src/_to_consumable_array.mjs";
import invariant from "tiny-invariant";
import { ONE, TradeType, ZERO } from "../constants";
import { computePriceImpact, sortedInsert } from "../utils";
import { CurrencyAmount } from "./fractions/currencyAmount";
import { Fraction } from "./fractions/fraction";
import { Price } from "./fractions/price";
import { Route } from "./route";
// comparator function that allows sorting trades by their output amounts, in decreasing order, and then input amounts
// in increasing order. i.e. the best trades have the most outputs for the least inputs and are sorted first
export function inputOutputComparator(a, b) {
    // must have same input and output token for comparison
    invariant(a.inputAmount.currency.equals(b.inputAmount.currency), "INPUT_CURRENCY");
    invariant(a.outputAmount.currency.equals(b.outputAmount.currency), "OUTPUT_CURRENCY");
    if (a.outputAmount.equalTo(b.outputAmount)) {
        if (a.inputAmount.equalTo(b.inputAmount)) {
            return 0;
        }
        // trade A requires less input than trade B, so A should come first
        if (a.inputAmount.lessThan(b.inputAmount)) {
            return -1;
        } else {
            return 1;
        }
    } else {
        // tradeA has less output than trade B, so should come second
        if (a.outputAmount.lessThan(b.outputAmount)) {
            return 1;
        } else {
            return -1;
        }
    }
}
// extension of the input output comparator that also considers other dimensions of the trade in ranking them
export function tradeComparator(a, b) {
    var ioComp = inputOutputComparator(a, b);
    if (ioComp !== 0) {
        return ioComp;
    }
    // consider lowest slippage next, since these are less likely to fail
    if (a.priceImpact.lessThan(b.priceImpact)) {
        return -1;
    } else if (a.priceImpact.greaterThan(b.priceImpact)) {
        return 1;
    }
    // finally consider the number of hops since each hop costs gas
    return a.route.path.length - b.route.path.length;
}
/**
 * Represents a trade executed against a list of pairs.
 * Does not account for slippage, i.e. trades that front run this trade and move the price.
 */ export var Trade = /*#__PURE__*/ function() {
    "use strict";
    function Trade(route, amount, tradeType) {
        _class_call_check(this, Trade);
        this.route = route;
        this.tradeType = tradeType;
        var tokenAmounts = new Array(route.path.length);
        if (tradeType === TradeType.EXACT_INPUT) {
            invariant(amount.currency.equals(route.input), "INPUT");
            tokenAmounts[0] = amount.wrapped;
            for(var i = 0; i < route.path.length - 1; i++){
                var pair = route.pairs[i];
                var ref = _sliced_to_array(pair.getOutputAmount(tokenAmounts[i]), 1), outputAmount = ref[0];
                tokenAmounts[i + 1] = outputAmount;
            }
            this.inputAmount = CurrencyAmount.fromFractionalAmount(route.input, amount.numerator, amount.denominator);
            this.outputAmount = CurrencyAmount.fromFractionalAmount(route.output, tokenAmounts[tokenAmounts.length - 1].numerator, tokenAmounts[tokenAmounts.length - 1].denominator);
        } else {
            invariant(amount.currency.equals(route.output), "OUTPUT");
            tokenAmounts[tokenAmounts.length - 1] = amount.wrapped;
            for(var i1 = route.path.length - 1; i1 > 0; i1--){
                var pair1 = route.pairs[i1 - 1];
                var ref1 = _sliced_to_array(pair1.getInputAmount(tokenAmounts[i1]), 1), inputAmount = ref1[0];
                tokenAmounts[i1 - 1] = inputAmount;
            }
            this.inputAmount = CurrencyAmount.fromFractionalAmount(route.input, tokenAmounts[0].numerator, tokenAmounts[0].denominator);
            this.outputAmount = CurrencyAmount.fromFractionalAmount(route.output, amount.numerator, amount.denominator);
        }
        this.executionPrice = new Price(this.inputAmount.currency, this.outputAmount.currency, this.inputAmount.quotient, this.outputAmount.quotient);
        this.priceImpact = computePriceImpact(route.midPrice, this.inputAmount, this.outputAmount);
    }
    var _proto = Trade.prototype;
    /**
   * Get the minimum amount that must be received from this trade for the given slippage tolerance
   * @param slippageTolerance tolerance of unfavorable slippage from the execution price of this trade
   */ _proto.minimumAmountOut = function minimumAmountOut(slippageTolerance) {
        invariant(!slippageTolerance.lessThan(ZERO), "SLIPPAGE_TOLERANCE");
        if (this.tradeType === TradeType.EXACT_OUTPUT) {
            // return this.outputAmount
            var slippageAdjustedAmountOut = new Fraction(ONE).add(slippageTolerance).invert().multiply(this.outputAmount.quotient).quotient;
            return CurrencyAmount.fromRawAmount(this.outputAmount.currency, slippageAdjustedAmountOut);
        } else {
            var slippageAdjustedAmountOut1 = new Fraction(ONE).add(slippageTolerance).invert().multiply(this.outputAmount.quotient).quotient;
            return CurrencyAmount.fromRawAmount(this.outputAmount.currency, slippageAdjustedAmountOut1);
        }
    };
    /**
   * Get the maximum amount in that can be spent via this trade for the given slippage tolerance
   * @param slippageTolerance tolerance of unfavorable slippage from the execution price of this trade
   */ _proto.maximumAmountIn = function maximumAmountIn(slippageTolerance) {
        invariant(!slippageTolerance.lessThan(ZERO), "SLIPPAGE_TOLERANCE");
        if (this.tradeType === TradeType.EXACT_INPUT) {
            return this.inputAmount;
        // const slippageAdjustedAmountIn = new Fraction(ONE)
        //   .add(slippageTolerance)
        //   .multiply(this.inputAmount.quotient).quotient
        // return CurrencyAmount.fromRawAmount(this.inputAmount.currency, slippageAdjustedAmountIn)
        } else {
            var slippageAdjustedAmountIn = new Fraction(ONE).add(slippageTolerance).multiply(this.inputAmount.quotient).quotient;
            return CurrencyAmount.fromRawAmount(this.inputAmount.currency, slippageAdjustedAmountIn);
        }
    };
    /**
   * Return the execution price after accounting for slippage tolerance
   * @param slippageTolerance the allowed tolerated slippage
   */ _proto.worstExecutionPrice = function worstExecutionPrice(slippageTolerance) {
        return new Price(this.inputAmount.currency, this.outputAmount.currency, this.maximumAmountIn(slippageTolerance).quotient, this.minimumAmountOut(slippageTolerance).quotient);
    };
    /**
   * Constructs an exact in trade with the given amount in and route
   * @param route route of the exact in trade
   * @param amountIn the amount being passed in
   */ Trade.exactIn = function exactIn(route, amountIn) {
        return new Trade(route, amountIn, TradeType.EXACT_INPUT);
    };
    /**
   * Constructs an exact out trade with the given amount out and route
   * @param route route of the exact out trade
   * @param amountOut the amount returned by the trade
   */ Trade.exactOut = function exactOut(route, amountOut) {
        return new Trade(route, amountOut, TradeType.EXACT_OUTPUT);
    };
    /**
   * Given a list of pairs, and a fixed amount in, returns the top `maxNumResults` trades that go from an input token
   * amount to an output token, making at most `maxHops` hops.
   * Note this does not consider aggregation, as routes are linear. It's possible a better route exists by splitting
   * the amount in among multiple routes.
   * @param pairs the pairs to consider in finding the best trade
   * @param nextAmountIn exact amount of input currency to spend
   * @param currencyOut the desired currency out
   * @param maxNumResults maximum number of results to return
   * @param maxHops maximum number of hops a returned trade can make, e.g. 1 hop goes through a single pair
   * @param currentPairs used in recursion; the current list of pairs
   * @param currencyAmountIn used in recursion; the original value of the currencyAmountIn parameter
   * @param bestTrades used in recursion; the current list of best trades
   */ Trade.bestTradeExactIn = function bestTradeExactIn(pairs, currencyAmountIn, currencyOut) {
        var ref = arguments.length > 3 && arguments[3] !== void 0 ? arguments[3] : {}, _maxNumResults = ref.maxNumResults, maxNumResults = _maxNumResults === void 0 ? 3 : _maxNumResults, _maxHops = ref.maxHops, maxHops = _maxHops === void 0 ? 3 : _maxHops, // used in recursion.
        currentPairs = arguments.length > 4 && arguments[4] !== void 0 ? arguments[4] : [], nextAmountIn = arguments.length > 5 && arguments[5] !== void 0 ? arguments[5] : currencyAmountIn, bestTrades = arguments.length > 6 && arguments[6] !== void 0 ? arguments[6] : [];
        invariant(pairs.length > 0, "PAIRS");
        invariant(maxHops > 0, "MAX_HOPS");
        invariant(currencyAmountIn === nextAmountIn || currentPairs.length > 0, "INVALID_RECURSION");
        var amountIn = nextAmountIn.wrapped;
        var tokenOut = currencyOut.wrapped;
        for(var i = 0; i < pairs.length; i++){
            var pair = pairs[i];
            // pair irrelevant
            if (!pair.token0.equals(amountIn.currency) && !pair.token1.equals(amountIn.currency)) continue;
            if (pair.reserve0.equalTo(ZERO) || pair.reserve1.equalTo(ZERO)) continue;
            var amountOut = void 0;
            try {
                var ref1;
                ref1 = _sliced_to_array(pair.getOutputAmount(amountIn), 1), amountOut = ref1[0], ref1;
            } catch (error) {
                // input too low
                if (error.isInsufficientInputAmountError) {
                    continue;
                }
                throw error;
            }
            // we have arrived at the output token, so this is the final trade of one of the paths
            if (amountOut.currency.equals(tokenOut)) {
                sortedInsert(bestTrades, new Trade(new Route(_to_consumable_array(currentPairs).concat([
                    pair
                ]), currencyAmountIn.currency, currencyOut), currencyAmountIn, TradeType.EXACT_INPUT), maxNumResults, tradeComparator);
            } else if (maxHops > 1 && pairs.length > 1) {
                var pairsExcludingThisPair = pairs.slice(0, i).concat(pairs.slice(i + 1, pairs.length));
                // otherwise, consider all the other paths that lead from this token as long as we have not exceeded maxHops
                Trade.bestTradeExactIn(pairsExcludingThisPair, currencyAmountIn, currencyOut, {
                    maxNumResults: maxNumResults,
                    maxHops: maxHops - 1
                }, _to_consumable_array(currentPairs).concat([
                    pair
                ]), amountOut, bestTrades);
            }
        }
        return bestTrades;
    };
    /**
   * similar to the above method but instead targets a fixed output amount
   * given a list of pairs, and a fixed amount out, returns the top `maxNumResults` trades that go from an input token
   * to an output token amount, making at most `maxHops` hops
   * note this does not consider aggregation, as routes are linear. it's possible a better route exists by splitting
   * the amount in among multiple routes.
   * @param pairs the pairs to consider in finding the best trade
   * @param currencyIn the currency to spend
   * @param nextAmountOut the exact amount of currency out
   * @param maxNumResults maximum number of results to return
   * @param maxHops maximum number of hops a returned trade can make, e.g. 1 hop goes through a single pair
   * @param currentPairs used in recursion; the current list of pairs
   * @param currencyAmountOut used in recursion; the original value of the currencyAmountOut parameter
   * @param bestTrades used in recursion; the current list of best trades
   */ Trade.bestTradeExactOut = function bestTradeExactOut(pairs, currencyIn, currencyAmountOut) {
        var ref = arguments.length > 3 && arguments[3] !== void 0 ? arguments[3] : {}, _maxNumResults = ref.maxNumResults, maxNumResults = _maxNumResults === void 0 ? 3 : _maxNumResults, _maxHops = ref.maxHops, maxHops = _maxHops === void 0 ? 3 : _maxHops, // used in recursion.
        currentPairs = arguments.length > 4 && arguments[4] !== void 0 ? arguments[4] : [], nextAmountOut = arguments.length > 5 && arguments[5] !== void 0 ? arguments[5] : currencyAmountOut, bestTrades = arguments.length > 6 && arguments[6] !== void 0 ? arguments[6] : [];
        invariant(pairs.length > 0, "PAIRS");
        invariant(maxHops > 0, "MAX_HOPS");
        invariant(currencyAmountOut === nextAmountOut || currentPairs.length > 0, "INVALID_RECURSION");
        var amountOut = nextAmountOut.wrapped;
        var tokenIn = currencyIn.wrapped;
        for(var i = 0; i < pairs.length; i++){
            var pair = pairs[i];
            // pair irrelevant
            if (!pair.token0.equals(amountOut.currency) && !pair.token1.equals(amountOut.currency)) continue;
            if (pair.reserve0.equalTo(ZERO) || pair.reserve1.equalTo(ZERO)) continue;
            var amountIn = void 0;
            try {
                var ref1;
                ref1 = _sliced_to_array(pair.getInputAmount(amountOut), 1), amountIn = ref1[0], ref1;
            } catch (error) {
                // not enough liquidity in this pair
                if (error.isInsufficientReservesError) {
                    continue;
                }
                throw error;
            }
            // we have arrived at the input token, so this is the first trade of one of the paths
            if (amountIn.currency.equals(tokenIn)) {
                sortedInsert(bestTrades, new Trade(new Route([
                    pair
                ].concat(_to_consumable_array(currentPairs)), currencyIn, currencyAmountOut.currency), currencyAmountOut, TradeType.EXACT_OUTPUT), maxNumResults, tradeComparator);
            } else if (maxHops > 1 && pairs.length > 1) {
                var pairsExcludingThisPair = pairs.slice(0, i).concat(pairs.slice(i + 1, pairs.length));
                // otherwise, consider all the other paths that arrive at this token as long as we have not exceeded maxHops
                Trade.bestTradeExactOut(pairsExcludingThisPair, currencyIn, currencyAmountOut, {
                    maxNumResults: maxNumResults,
                    maxHops: maxHops - 1
                }, [
                    pair
                ].concat(_to_consumable_array(currentPairs)), amountIn, bestTrades);
            }
        }
        return bestTrades;
    };
    return Trade;
}();
